import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { useQuery } from 'react-query';
import {
  Col,
  Form,
  Input,
  InputNumber,
  notification,
  Popconfirm,
  Row,
} from 'antd';
import { useForm } from 'antd/lib/form/Form';
import { indexBy, setValueByNamePath } from 'utils/utils2';
import {
  getParAssetUseConditionListByUseID,
  ParAssetUseConditionResponse,
  addParAssetUseCondition,
  removeParAssetUseCondition,
  ParAssetUseConditionRequest,
  updateParAssetUseCondition,
} from 'services/pricing';
import { PlusOutlined } from '@ant-design/icons';
import DataGrid, { DataGridColDef } from 'components/DataGrid/DataGrid';
import DeleteButton from 'components/DeleteButton';
import { useFormList } from 'hooks/useFormList';
import DF from 'DisplayFramework';
import ButtonOperation from 'components/ButtonOperation';
import SaveButton from 'components/SaveButtonAction';
import ProductInput from 'pages/ProposalV3/PropDetail/ProductInput';
import OfferInput from '../../Management/ParametersFiltering/OfferInput';
import PricingInput from 'pages/ProposalV3/PropDetail/PricingInput';
import FormInput from 'components/FormInput';
import PricingListFilteredSelect from 'components/PricingListFilteredSelect';
import Page, { PageModalProps } from 'components/Page';
import { isNil } from 'lodash';
import { AuthContext } from 'components/Auth/Auth';
import getEnvValue from 'utils/getEnvValue';
import {
  AssetUseCriteria,
  FilteringCriteria,
  prFilteringCriteria,
} from 'services/pricing';
//import OfferInput from 'pages/ProposalV3/PropDetail/OfferInput';

const NEW_ACTION_ID = -1;
export type ParAssetConditionListProps = PageModalProps & {
  useid: number;
  mgid: number;
  refetch?: () => void;
};
const ParAssetConditionList = (props: ParAssetConditionListProps) => {
  const { visible = false, onCancel, showAsModal = false } = props;
  const [form] = useForm();
  const { isInspect } = useContext(AuthContext);
  const fieldName = 'ParAssetCondition';
  const { add, remove } = useFormList(form, fieldName, {
    idKey: 'condid',
    addAsFirst: true,
  });

  const { data: conditionsList, refetch } = useQuery(
    ['getParAssetUseConditionListByUseID', { useid: props.useid }],
    () =>
      getParAssetUseConditionListByUseID({ useid: Number(props.useid) }).then(
        (res) => {
          return res.list || [];
        }
      ),
    {
      refetchOnMount: false,
      enabled: Number(props.useid) !== 0 || isNil(props.useid),
    }
  );
  useEffect(() => {
    if (conditionsList) {
      form.setFieldsValue({
        [fieldName]: conditionsList,
      });
    }
  }, [form, conditionsList]);
  const getName = useCallback(
    (name: string) => (record: ParAssetUseConditionResponse) => {
      const actions = form.getFieldValue(fieldName);
      const index = indexBy(actions, 'condid')[record.condid];
      return [fieldName, index, name];
    },
    [form]
  );
  const onSave = useCallback(
    (record: ParAssetUseConditionResponse) => {
      const condition: any[] = form.getFieldValue(fieldName);
      const index = indexBy(condition, 'condid')[record.condid];
      const currentValue = {
        ...condition[index],
        ofrid:
          typeof condition[index].ofrid === 'object'
            ? condition[index].ofrid?.ofrid
            : condition[index].ofrid,
        ofrtype: condition[index].ofrtype,
        priidexceed:
          typeof condition[index].priidexceed === 'object'
            ? condition[index].priidexceed?.priid
            : condition[index].priidexceed,
      };
      const { condid } = currentValue;

      if (condid === NEW_ACTION_ID) {
        addParAssetUseCondition({
          parassetUseReq: { ...currentValue, condid: null as any },
        }).then((response) => {
          if (response.code === '200') {
            notification.success({ message: response.message });
          }
          refetch();
        });
      } else {
        return updateParAssetUseCondition({
          condid: Number(record.condid),
          request: currentValue,
        }).then((response) => {
          if (response.code === '200') {
            notification.success({
              message: response.message,
            });

            refetch();
          } else {
            notification.error({
              message: response.message,
            });
          }
        });
      }
    },
    [form, refetch]
  );
  const getFilterList =(prcode:any,offerid:any): prFilteringCriteria => {
    let assetList = form?.getFieldValue('assets');

    let assetUseCriteriaList: Array<AssetUseCriteria> = [];
    let offerCriteriaList: Array<FilteringCriteria> = [];
    let ret: prFilteringCriteria;
    offerCriteriaList.push({
      critcode: 'OFFER',
      critvalue: offerid?? '0',
    });
    offerCriteriaList.push({
      critcode: 'PRCODE',
      critvalue: prcode ?? '',
    });
    offerCriteriaList.push({
      critcode: 'TPTYPE',
      critvalue: form.getFieldValue('client')?.tptype ?? '',
    });
    offerCriteriaList.push({
      critcode: 'NETWORK',
      critvalue: form.getFieldValue('ctnetworkcode') ?? '',
    });
    offerCriteriaList.push({
      critcode: 'CLIRATING',
      critvalue: form.getFieldValue('client')?.tpsalestatus ?? '',
    });
    assetList?.foreach((a: any) => {
      offerCriteriaList.push({
        critcode: 'ASSETFAM',
        critvalue: a.assetfamily,
      });
      offerCriteriaList.push({ critcode: 'ASSETTYPE', critvalue: a.assettype });
      offerCriteriaList.push({
        critcode: 'ASSETCAT',
        critvalue: a.assetcategory,
      });

      offerCriteriaList.push({ critcode: 'POWER', critvalue: a.power ?? '' });
      // additional attributes :
      a.entityAddAttributList?.forEach((e: any) => {
        offerCriteriaList.push({
          critcode: e.attcode,
          critvalue: e.attstringvalue ?? e.attnumericvalue,
        });
      });

      if (getEnvValue('FLAG_APPLY_ASSET_USE')) {
        a.assetusereqList?.forEach((u: any) => {
          assetUseCriteriaList.push({
            assetfamily: a.assetfamily,
            assettype: a.assettype,
            assetcategory: a.assetcategory,
            catalogid: a.catalogid ?? '',
            attcode: u.attcode ?? '',
            attvalue: u.usevalue ?? '',
          });
        });
      }
    });

    ret = {
      prcode: form?.getFieldValue('flagmasterfacility')
        ? form?.getFieldValue('mfprcode')
        : form?.getFieldValue('prcode') ?? '',
      tpid: form.getFieldValue('client')?.tpid ?? 0,
      tpidbroker:form.getFieldValue('broker')?.tpid ?? 0,
      tpidmanufacturer:assetList?assetList[0]?.tpidmanufacturer??0:0,
      assetUseCriteria: assetUseCriteriaList,
      assetRVCriteria:[],
      offerCriteria: offerCriteriaList,
      flagApplyAssetUse: getEnvValue('FLAG_APPLY_ASSET_USE'),
      flagApplyAttributesExtendDurationValues: true,
      flagApplyAttributesExtendFirstRentalValues: true,
      flagApplyAttributesExtendRVValues: true,
      flagApplyAttributesExtendRateValues: true,
      flagApplyExtendDurationValues: true,
      flagApplyExtendFirstRentalValues: true,
      flagApplyExtendRVValues: true,
      flagApplyExtendRateValues: true,
    };
    return ret;
  };
  const columns = useMemo<DataGridColDef<ParAssetUseConditionResponse>[]>(
    () => [
      {
        dataIndex: 'condid',
        render: (value, record, index) => {
          return record?.condid === NEW_ACTION_ID ? (
            <Form.Item
              noStyle
              name={getName('condid')(record)}
              rules={[{ required: true }]}
            >
              <Input disabled />
            </Form.Item>
          ) : (
            <Form.Item>{value}</Form.Item>
          );
        },
        width: 50,
        fixed: true,
      },
      {
        dataIndex: 'prcode',

        render: (value, record) => (
          <Form.Item
            noStyle
            name={getName('prcode')(record)}
            rules={[{ required: true }]}
          >
            <ProductInput
              onSelect={(e) => {
                setValueByNamePath(form, getName('prcode')(record), e);
                setValueByNamePath(form, getName('offerid')(record), '');
                setValueByNamePath(form, getName('ofrid')(record), '');
                setValueByNamePath(form, getName('ofrtypelabel')(record), '');
              }}
            />
          </Form.Item>
        ),
        width: 200,
      },
      {
        dataIndex: 'offerid',

        render: (value, record) => (
          <Form.Item
            noStyle
            name={getName('offerid')(record)}
            rules={[{ required: true }]}
          >
            <OfferInput
              params={{
                prcode: form.getFieldValue(getName('prcode')(record)),
                mgid: props.mgid,
              }}
              allData
              onSelect={(e) => {
                setValueByNamePath(form, getName('offerid')(record), e);
                setValueByNamePath(form, getName('ofrid')(record), '');
                setValueByNamePath(form, getName('ofrtypelabel')(record), '');
              
              }}
            />
            {/* <OfferInput
              allowClear
              params={{
                prcode: form.getFieldValue(getName('prcode')(record)),
                filtersList: {
                  prcode: form.getFieldValue(getName('prcode')(record)),
                  assetUseCriteria: [],
                  offerCriteria: [],
                  tpid: 0,
                },
                filters: '',
                finamount: 0,
                duration:0,
                searchMode: 'PRCODE',
              }}
            /> */}
          </Form.Item>
        ),
        width: 200,
      },
      {
        dataIndex: 'ofrid',

        render: (value, record) => (
          <Form.Item
            noStyle
            name={getName('ofrid')(record)}
            rules={[{ required: true }]}
          >
            <PricingInput
              params={{
                catalogid: 0,
                duration: 0,
                filters: '',
                filtersList: getFilterList(form.getFieldValue(getName('prcode')(record)), form.getFieldValue(getName('offerid')(record))),
                finamount: 0,
                mgid:Number(localStorage.getItem('mgid')) ,
                offerid:  form.getFieldValue(getName('offerid')(record)),
                prcode: form.getFieldValue(getName('prcode')(record))
              }}
              onChange={(e) => {
                setValueByNamePath(form, getName('ofrtype')(record), e.ofrtype);

                // setValueByNamePath(form, getName('ofrid')(record), e.ofrid);
              }}
            />
          </Form.Item>
        ),
        width: 200,
      },
      {
        dataIndex: 'ofrtypelabel',
        render: (value, record) => (
          <Form.Item
            noStyle
            name={getName('ofrtype')(record)}
            rules={[{ required: true }]}
          >
            <Input disabled />
          </Form.Item>
        ),
        width: 200,
      },
      {
        dataIndex: 'priidexceed',
        render: (value, record) => (
          <Form.Item shouldUpdate noStyle>
            {({ getFieldValue }) => {
              return (
                <FormInput
                  col
                  xs={24}
                  md={24}
                  name={getName('priidexceed')(record)}
                  noStyle
                >
                  <PricingListFilteredSelect
                    allowClear
                    params={{
                      offerid: getFieldValue(getName('offerid')(record)),
                      ctetype: 'ASSUSE',
                      prcode: getFieldValue(getName('prcode')(record)),
                      entity: '',
                      entityid: 0,
                      tpid: 0,
                    }}
                  />
                </FormInput>
              );
            }}
          </Form.Item>
        ),
        width: 200,
      },

      {
        dataIndex: 'minvalue',
        render: (value, record) => (
          <Form.Item
            noStyle
            name={getName('minvalue')(record)}
            rules={[{ required: true }]}
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        ),
        width: 200,
      },
      {
        dataIndex: 'maxvalue',
        render: (value, record) => (
          <Form.Item
            noStyle
            name={getName('maxvalue')(record)}
            rules={[{ required: true }]}
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        ),
        width: 200,
      },
      {
        dataIndex: 'defaultvalue',

        render: (value, record) => (
          <Form.Item
            noStyle
            name={getName('defaultvalue')(record)}
            rules={[{ required: true }]}
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        ),
        width: 200,
      },

      {
        dataIndex: 'Actions',
        render: (_, record, index) =>
          record?.condid === NEW_ACTION_ID ? (
            <Row gutter={8}>
              <Col span={18}>
                <SaveButton
                  comcode="save"
                  size="small"
                  onClick={() => !isInspect && onSave(record)}
                  htmlType="button"
                />
              </Col>
              <Col span={6}>
                <Popconfirm
                  title="Are you sure to delete this value?"
                  onConfirm={() => {
                    remove(record.condid);
                  }}
                  okText="Yes"
                  cancelText="No"
                >
                  <DeleteButton onlyIcon />
                </Popconfirm>
              </Col>
            </Row>
          ) : (
            <Row gutter={8}>
              <Col span={18}>
                <SaveButton
                  comcode="update"
                  size="small"
                  onClick={() => !isInspect && onSave(record)}
                  htmlType="button"
                />
              </Col>
              <Col span={6}>
                <Popconfirm
                  title="Are you sure to delete this Condition?"
                  onConfirm={() => {
                    removeParAssetUseCondition({
                      condid: record.condid,
                    }).then((res) => {
                      notification.success({ message: res.message });
                      refetch();
                    });
                  }}
                >
                  <DeleteButton onlyIcon />
                </Popconfirm>
              </Col>
            </Row>
          ),
        width: 160,
        fixed: 'right',
      },
    ],

    [form, getName, onSave, props.mgid, refetch, remove]
  );
  const addRow = () => {
    const row: ParAssetUseConditionRequest = {
      condid: NEW_ACTION_ID,
      defaultvalue: 0,
      maxvalue: 0,
      minvalue: 0,
      offerid: 0,
      ofrid: '' as any,
      ofrtype: '',
      prcode: '',
      priidexceed: 0,
      useid: props.useid,
    };
    add(row);
  };
  return (
    <DF.Provider pagcode="parassetconditionlist">
      <Page
        modalTitle="AssetConditionList"
        showAsModal={showAsModal}
        visible={visible}
        onCancel={() => {
          onCancel!();
        }}
      >
        <Form form={form}>
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) => {
              const condition: ParAssetUseConditionResponse[] =
                getFieldValue(fieldName);
              const isNewItemInTable =
                condition?.find(({ useid }) => useid === NEW_ACTION_ID) !==
                undefined;
              return (
                <DataGrid
                  titleComcode="ParAssetConditionList"
                  rowKey="useid"
                  columns={columns}
                  dataSource={condition}
                  hideFilters
                  extraActions={
                    <ButtonOperation
                      comcode="Add"
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() => !isInspect && addRow()}
                      disabled={isNewItemInTable}
                    />
                  }
                />
              );
            }}
          </Form.Item>
        </Form>
      </Page>
    </DF.Provider>
  );
};

export default ParAssetConditionList;
